/**
 * The answer every field check gives (shared-react-ui#81, shared-audit#17):
 * `ok`, the `value` to send (normalised), and — when it is not ok — a
 * `problem` `{code, params}` a page turns into a sentence with
 * `fieldMessage`.
 *
 * The codes are a CONTRACT with every server: shared-pweb-common's
 * `pweb_common.fields` returns the same ones for the same input (the shared
 * `cases.json` proves it), and `refuse()` puts them on the wire as
 * `{error, code, field, limit}`. Add a code; never rename or reuse one.
 */

/** Every code a check can return — `pweb_common.fields.CODES`, in the same order. */
export const FIELD_CODES = [
  'name.required',
  'name.too_long',
  'name.invalid_characters',
  'name.needs_letter',
  'name.web_address',
  'business_name.required',
  'business_name.too_short',
  'business_name.too_long',
  'business_name.invalid_characters',
  'email.required',
  'email.too_long',
  'email.spaces',
  'email.no_at',
  'email.several_at',
  'email.invalid_local_part',
  'email.invalid_domain',
  'email.invalid',
  'phone.required',
  'phone.invalid_characters',
  'phone.needs_country_code',
  'phone.unknown_country_code',
  'phone.country_mismatch',
  'phone.too_long',
  'phone.too_short',
  'phone.not_mobile',
  'phone.not_valid',
  'url.required',
  'url.too_long',
  'url.invalid',
  'url.not_public',
  'url.not_https',
  'text.required',
  'text.too_long',
  'text.too_short',
  'text.invalid_characters',
] as const;

export type FieldCode = (typeof FIELD_CODES)[number];

/** A number we text (`mobile`) or one we only record (`any`). */
export type PhoneKind = 'mobile' | 'any';

/**
 * The facts a sentence needs. Which keys are present depends on the code:
 * `max` / `min` for a limit (`count` for free text), and for a phone the
 * `kind`, and — when a country was chosen or read — `country` (ISO), `dial`
 * ('+44'), `min`, `max` (national digits after the code), `trunk` ('0' or
 * ''), `prefixes` (a mobile range as dialled at home, e.g. ['07']),
 * `otherDial` / `otherCountry` (the code a number started with instead),
 * `startsWith` (on a `phone.not_valid`: the digits no number there begins
 * with after the code, '00'; absent for North America's area-code rule).
 */
export interface FieldParams {
  max?: number;
  min?: number;
  count?: number;
  kind?: PhoneKind;
  country?: string;
  dial?: string;
  trunk?: string;
  prefixes?: string[];
  otherDial?: string;
  otherCountry?: string;
  startsWith?: string;
}

export interface FieldProblem {
  code: FieldCode;
  params: FieldParams;
}

/**
 * `ok` and the `value` to send, or the `problem`. On a refusal `value` is
 * the input trimmed — never a stored form.
 */
export type FieldResult =
  | { ok: true; value: string; problem: null }
  | { ok: false; value: string; problem: FieldProblem };

/** Every check takes this: `required: false` makes nothing typed `ok` with `''`. */
export interface RequiredOption {
  required?: boolean;
}

/** The limit a problem crossed: `max` for a `*.too_long`, `min` for a `*.too_short`. */
export function problemLimit(problem: FieldProblem): number | undefined {
  if (problem.code.endsWith('.too_long')) return problem.params.max;
  if (problem.code.endsWith('.too_short')) return problem.params.min;
  return undefined;
}
